import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService, SessionStorageService } from "ngx-webstorage";
import { Observable, of, ReplaySubject } from "rxjs";
import { catchError, finalize, shareReplay, tap, first } from "rxjs/operators";
import { UsuarioRestService } from "src/app/modules/ficha-usuario/services/usuario-rest.service";
import { environment } from "src/environments/environment";
import { Account } from "../user/account.model";
import { AuthSSOProvider } from "./sso-jwt.service";
import { StateStorageService } from "./state-storage.service";
import * as moment from 'moment';
import { DateAdapter } from '@angular/material';

export const TOKEN_STORAGE = "authenticationToken";

@Injectable({ providedIn: "root" })
export class AccountService {
  public userIdentity: Account | null = null;
  private authenticationState = new ReplaySubject<Account | null>(1);
  private accountCache$?: Observable<Account | null>;

  constructor(
    private translate: TranslateService,
    private sessionStorage: SessionStorageService,
    private localStorage: LocalStorageService,
    private http: HttpClient,
    private stateStorageService: StateStorageService,
    private router: Router,
    private authProvider: AuthSSOProvider,
    private usuarioService: UsuarioRestService,
    private dateAdapter: DateAdapter<any>,
  ) {}

  resolveAccount() {
    // Si detectamos que hemos sido redirigidos por el SSO, obtenemos el token devuelto si viene en la redirección
    if (this.authProvider.checkSSOAuthenticationSuccess()) {
      let token = this.authProvider.getToken();
      this.sessionStorage.store(TOKEN_STORAGE, token);

      // Forzamos a obtener una nueva identidad con el token recibido
      this.identity(true).subscribe();
    } else if (!!this.getToken() && this.getToken() !== "") {
      this.identity(true).subscribe();
    } else {
      // Si no tenemos identidad entonces redirigimos al SSO para autenticar al usuario y obtener un token
      if (!this.isAuthenticated()) {
        this.authProvider.redirectoToSSO(
          environment.ssoURL,
          environment.ssoRealm
        );
      }
    }
  }

  getToken(): string {
    return (
      this.localStorage.retrieve(TOKEN_STORAGE) ||
      this.sessionStorage.retrieve(TOKEN_STORAGE) ||
      ""
    );
  }

  private authenticate(identity: Account | null): void {
    if (identity != null) {
      this.userIdentity = identity;
      this.authenticationState.next(this.userIdentity);
    } else {
      this.authProvider.redirectoToSSO(
        environment.ssoURL,
        environment.ssoRealm
      );
    }
  }

  identity(force?: boolean): Observable<Account | null> {
    if (!this.accountCache$ || force || !this.isAuthenticated()) {
      this.accountCache$ = this.fetch().pipe(
        catchError(() => {
          return of(null);
        }),
        tap((account: Account | null) => {
          this.authenticate(account);

          // After retrieve the account info, the language will be changed to
          // the user's preferred language configured in the account setting
          if (account && account.langKey) {
            const langKey = this.sessionStorage.retrieve('locale') || account.langKey;
            this.translate.use(langKey).subscribe(() => {
              this.router.navigate(['/convocatorias']);

              // Cuando se cambie el idioma, se actualizarán los locales de Moment y del DateAdapter
              this.translate.onLangChange.subscribe((event) => {
                moment.locale(event.lang);
                this.dateAdapter.setLocale(event.lang);
                this.persistLangChange().pipe(first()).subscribe();
              });
            });
          }

          if (account) {
            this.navigateToStoredUrl();
          }
        }),
        shareReplay()
      );
    }
    return this.accountCache$;
  }

  refreshFlagEsUsuarioTelematico() {
    this.usuarioService
      .comprobarSiUsuarioIdentificadoEsSolicitante()
      .subscribe((esSolicitante) => {
        this.userIdentity.esSolicitante = esSolicitante;
      });
  }

  isAuthenticated(): boolean {
    return !!this.userIdentity && this.userIdentity !== null;
  }

  isAnonymous(): boolean {
    if (this.isAuthenticated()) {
      if (!!this.userIdentity.authType) {
        return (
          this.userIdentity.authType === null ||
          this.userIdentity.authType === "ANONIMO"
        );
      }
    }

    return true;
  }

  isGeneric(): boolean {
    if (this.isAuthenticated()) {
      if (!!this.userIdentity.authType) {
        return (
          this.userIdentity.authType === null ||
          this.userIdentity.authType === "GENERICO"
        );
      }
    }

    return true;
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.authenticationState.asObservable();
  }

  urlModificarUsuario(): string {
    const token =
      this.localStorage.retrieve(TOKEN_STORAGE) ||
      this.sessionStorage.retrieve(TOKEN_STORAGE);

    // IMPORTANTE: Dejar la redirectUrl para el final, ya que el hashing destruye los parametros que vienen por detrás
    const url: string =
      environment.ssoURL +
      "#/modify?i3jwt-token=" +
      token +
      "&language=" +
      this.translate.currentLang +
      "&realm=" +
      environment.ssoRealm +
      "&redirectUrl=" +
      window.location.href;

    return url;
  }

  logout(): void {
    const token =
      this.localStorage.retrieve(TOKEN_STORAGE) ||
      this.sessionStorage.retrieve(TOKEN_STORAGE);

    const headersParam: HttpHeaders = new HttpHeaders({
      Authorization: "Bearer " + token,
    });

    const parametros: HttpParams = new HttpParams().set(
      "realm",
      environment.ssoRealm
    );

    this.http
      .get<boolean>(environment.ssoBackEndUrl + "login/logout", {
        headers: headersParam,
        params: parametros,
      })
      .subscribe(
        (success) => {
          this.localStorage.clear(TOKEN_STORAGE);
          this.sessionStorage.clear(TOKEN_STORAGE);
          this.userIdentity = null;
        },
        (error) => {
          // TODO: Hacer algo en caso de error
        }
      );
  }

  redirectToSSO(): void {
    this.authProvider.redirectoToSSO(environment.ssoURL, environment.ssoRealm);
  }

  fetch(): Observable<Account> {
    return this.http.get<Account>(environment.apiURL + "account/getaccount");
  }

  private navigateToStoredUrl(): void {
    const previousUrl = this.stateStorageService.getUrl();
    if (previousUrl) {
      this.stateStorageService.clearUrl();
      this.router.navigateByUrl(previousUrl);
    }
  }

  persistLangChange(): Observable<boolean> {
    return this.http.get<boolean>(
      environment.apiURL + "v1/usuarios/cambiarIdioma"
    );
  }
}
